import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'app/store';

import { selectIndustries } from 'features/dictionaries/dictionariesSelectors';

import { RegistrationMetrics, User } from 'types/auth';

import { Region } from 'constants/enums';
import { ClientTiers } from 'constants/enums';

export const selectAuthToken = (state: RootState): string | null =>
  state.auth.token;
export const selectAuthRegion = (state: RootState): Region | null =>
  state.auth.region;
export const selectIsLoggedIn = (state: RootState): boolean =>
  state.auth.isLoggedIn;
export const selectUser = (state: RootState): User => state.auth.user;
export const selectIsLoading = (state: RootState): boolean =>
  state.auth.isLoading;
export const selectPasswordLength = (state: RootState): number =>
  state.auth.passwordLength;

export const selectClientTier = (state: RootState): ClientTiers | null =>
  state.auth.user?.clientTierConfiguration?.tier;

export const selectIsClientSelfService = (state: RootState): boolean =>
  state.auth.user?.clientTierConfiguration?.tier === ClientTiers.Concierge;

export const selectClientId = (state: RootState): string =>
  state.auth.user?.selectedClient;

export const selectClient = createSelector(
  [selectUser, selectClientId],
  (user, clientId) => user?.clients?.[clientId] || null
);

export const selectClientStripeId = createSelector(
  [selectClient],
  (client) => client?.stripeId || null
);

export const selectClientWorkspace = (state: RootState) => state.auth.workspace;

export const selectIsClientWorkspaceLoading = (state: RootState) =>
  state.auth.workspaceLoading;

export const selectClientIndustryName = createSelector(
  [selectClientWorkspace, selectIndustries],
  (workspace, industries) =>
    industries.find((item) => item.value === workspace?.industry)?.label ?? null
);

export const selectIsShowGuide = (state: RootState): boolean =>
  !state.auth.user?.dismissGuide;

export const selectIsJobsGuideCompleted = (state: RootState): boolean =>
  Boolean(state.auth?.user?.jobsGuideCompleted);

export const selectIsApplicationsGuideCompleted = (state: RootState): boolean =>
  Boolean(state.auth?.user?.applicationsGuideCompleted);

export const selectIsJobseekersGuideCompleted = (state: RootState): boolean =>
  Boolean(state.auth?.user?.jobseekersGuideCompleted);

export const selectGuideCompletedPageTours = createSelector(
  [selectUser],
  (user) => ({
    dismissGuide: user?.dismissGuide,
    jobsGuideCompleted: user?.jobsGuideCompleted,
    applicationsGuideCompleted: user?.applicationsGuideCompleted,
    jobseekersGuideCompleted: user?.jobseekersGuideCompleted
  })
);

export const selectRegistrationMetrics = (
  state: RootState
): RegistrationMetrics => {
  return state.auth.registrationMetrics;
};
